// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — mineriumProps2D.jsx   ·   THE FURNISHINGS KIT, 2D HALF
// ────────────────────────────────────────────────────────────────────
// `mineriumProps.jsx` draws a furnishing in three.js, for the yard.
// This draws the SAME furnishing in CSS, for the Hall — which is a 2D
// tile canvas and always has been.
//
// ⚑ WHY TWO FILES AND NOT ONE. A prop is stored as an `itemId` and a
// position, never as a model (PIERRE_PRIME L24 · ONE ID, MANY
// RENDERERS — the same shape the Mark's companion slot is built to).
// So a second surface does not need a second catalogue, a second shop
// or a second Firebase node. It needs a second RENDERER, and this is
// it. Anything that changes what a piece IS belongs in
// `mineriumCatalogue.js`; this file only ever decides how it LOOKS
// here.
//
// ── ⚠ THIS IS PLACEHOLDER ART AND IS MEANT TO BE ─────────────────────
// caP'n's standing build rule: scaffolding first, so the seams can be
// proven to function, content replaced after. Every recipe below is a
// two-tone standee with a painted silhouette. It reads as furniture, it sits
// in the Hall's existing visual language, and it is deliberately cheap
// to throw away. THE SEAM is the valuable part: `PROP_2D` keyed by art
// id, resolved through `artOf()`, mounted by one component.
//
// ── THE RULES THIS FILE OBEYS ───────────────────────────────────────
//   · NO KEYFRAMES. JSX <style> blocks are forbidden Empire-wide (the
//     J'aeonxi foil freeze trap) and `ensureKeyframes()` is the sanctioned
//     escape hatch. Neither is needed: every animated value here is
//     derived from the `frame` counter the Hall already ticks, exactly
//     as `TrophyVisual` does it. Zero new timers, zero new keyframes.
//   · NO PRNG IN A RENDER BODY. Nothing here is random at all.
//   · A MYTHICAL HAS NO RECIPE. `artOf()` resolves it to its Legendary
//     predecessor and the ascension layer goes over the top — the same
//     law the 3D kit is built to (L23), so "same design, risen" is not
//     a promise anybody has to keep, it is the only thing either
//     renderer can draw.
//   · A MISSING RECIPE RENDERS NOTHING. Not a grey box, not a crash.
//     `auditProp2D()` names the gap at boot instead.
// ════════════════════════════════════════════════════════════════════

import { ITEMS, AREAS, artOf, isAscended, SHELF_META } from "./mineriumCatalogue";

// ════════════════════════════════════════════════════════════════════
// THE RECIPES  —  one row per art id
// ────────────────────────────────────────────────────────────────────
//   w, h    fraction of a tile
//   lift    how far off the floor it sits (0 = flat on it)
//   body    the front face · top   the lit upper face
//   edge    the outline
//   parts   ⭐ the SILHOUETTE — [x, y, w, h, fill, radius?] in fractions
//           of the piece's own box, drawn front-to-back. Four rectangles
//           make a chair; three make a barrel. This is what replaced
//           `glyph`. `radius` is any CSS corner value — "50%" for a
//           canopy or a bowl. ⚠ WITHOUT IT everything organic reads as a
//           BLOCK: the first pass rendered the Sapling and the Anima
//           Tree as green rectangles on sticks (lollipops, not trees)
//           and the Chao Pool as a progress bar. Rendered and looked at
//           before shipping, which is the only reason that was caught.
//
// ⚠⚠ WHY `glyph` IS GONE, because it will look like a regression in a
// diff and it is the opposite. Every row used to carry an EMOJI — 🪑 🛢
// 🛋 ⛲ 🚪 — which the browser renders in the VENDOR'S OWN bitmap font
// (Apple Color Emoji, Noto Color Emoji). That is a photograph of
// somebody else's sofa, in a game whose foundational art law is that
// everything is primitives and painted canvas and NOTHING is an external
// asset. Walk past the Void Bench in the yard — five dark slabs and a
// violet seat, modelled — step through the archway, and the same
// `itemId` was a beige IKEA pictogram. One door apart.
// ⛑ A silhouette in the right two tones beats a foreign-style pictogram
// every single time, and it costs no assets, no timers and no keyframes.
// Found by adversarial review 2026-09-14.
//   flat    true for things you walk over (rugs, path) — drawn UNDER
//           everything and never given a shadow or a standee
// ════════════════════════════════════════════════════════════════════
export const PROP_2D = {
  // ── FABLE ─────────────────────────────────────────────────────────
  crop_bed:     { w: .84, h: .42, lift: 0,    body: "#2e3d1c", top: "#465a2a", edge: "#6d8440", parts: [[.02,.52,.96,.48,"b","3px"],[.07,.26,.14,.34,"t","50% 50% 20% 20%"],[.30,.16,.14,.42,"t","50% 50% 20% 20%"],[.55,.22,.14,.38,"t","50% 50% 20% 20%"],[.79,.30,.14,.30,"t","50% 50% 20% 20%"]] },
  dawg_bowl:    { w: .52, h: .26, lift: 0,    body: "#4a3a22", top: "#6b5433", edge: "#8d6f44", parts: [[.06,.34,.88,.66,"b","10% 10% 46% 46%"],[.14,.24,.72,.26,"t","50%"],[.28,.32,.44,.12,"e","50%"]] },
  wooden_chair: { w: .46, h: .62, lift: 0,    body: "#4a3524", top: "#6a4d34", edge: "#8a6b46", parts: [[.16,.00,.68,.52,"t"],[.10,.50,.80,.13,"e"],[.14,.63,.12,.37,"b"],[.74,.63,.12,.37,"b"]] },
  straw_rug:    { w: .88, h: .58, lift: 0,    body: "#6a5a32", top: "#8a7642", edge: "#a98f52",    flat: true, parts: [[.00,.00,1,1,"b"],[.08,.16,.84,.10,"t"],[.08,.46,.84,.10,"t"],[.08,.74,.84,.10,"t"]] },
  fence_post:   { w: .20, h: .66, lift: 0,    body: "#3f2f1e", top: "#5c452c", edge: "#7a5c3a", parts: [[.28,.10,.44,.90,"b"],[.20,.00,.60,.12,"t"],[.00,.32,1,.10,"e"],[.00,.62,1,.10,"e"]] },
  lantern_post: { w: .26, h: .82, lift: 0,    body: "#2a2a30", top: "#3e3e46", edge: "#d4a03c", lamp: "#f2c14e", parts: [[.42,.28,.16,.72,"b"],[.20,.02,.60,.28,"e","18%"],[.30,.08,.40,.16,"t","24%"]] },
  stone_path:   { w: .92, h: .62, lift: 0,    body: "#3a3a3e", top: "#54545a", edge: "#6a6a72",    flat: true, parts: [[.02,.06,.44,.38,"b"],[.52,.02,.44,.40,"t"],[.06,.54,.40,.40,"t"],[.54,.56,.42,.38,"b"]] },
  hay_bale:     { w: .70, h: .48, lift: 0,    body: "#6a5824", top: "#8d7634", edge: "#ab9146", parts: [[.04,.14,.92,.86,"b","8%"],[.04,.14,.92,.20,"t","8% 8% 0 0"],[.22,.32,.05,.62,"e"],[.72,.32,.05,.62,"e"]] },
  sapling:      { w: .34, h: .78, lift: 0,    body: "#2f3d1e", top: "#4a6a2c", edge: "#6d9440", parts: [[.45,.58,.10,.42,"b"],[.10,.20,.80,.46,"e","48% 48% 40% 40%"],[.22,.04,.56,.38,"e","50%"],[.34,.26,.32,.24,"t","50%"]] },
  flower_bed:   { w: .74, h: .34, lift: 0,    body: "#33401f", top: "#4c5f2c", edge: "#8a5f9a", parts: [[.00,.52,1,.48,"b","4px"],[.10,.10,.18,.38,"e","50%"],[.41,.02,.18,.42,"e","50%"],[.72,.16,.18,.34,"e","50%"]] },
  barrel:       { w: .46, h: .54, lift: 0,    body: "#4a3524", top: "#6a4d34", edge: "#8a6b46", parts: [[.10,.04,.80,.96,"b"],[.10,.04,.80,.14,"t"],[.06,.30,.88,.09,"e"],[.06,.66,.88,.09,"e"]] },
  torch:        { w: .18, h: .72, lift: 0,    body: "#33261a", top: "#4a3726", edge: "#d4732c", lamp: "#ff9a3c", parts: [[.38,.28,.24,.72,"b"],[.24,.00,.52,.34,"e","50% 50% 40% 40%"],[.36,.06,.28,.18,"t","50%"]] },
  bird_bath:    { w: .54, h: .50, lift: 0,    body: "#43434a", top: "#5e5e68", edge: "#8a8a96", parts: [[.40,.48,.20,.52,"b"],[.28,.86,.44,.14,"t","40%"],[.06,.20,.88,.30,"t","46%"],[.18,.26,.64,.16,"e","50%"]] },
  sign_post:    { w: .52, h: .62, lift: 0,    body: "#4a3524", top: "#6a4d34", edge: "#8a6b46", parts: [[.44,.38,.12,.62,"b"],[.06,.02,.88,.40,"t"],[.14,.12,.72,.06,"e"],[.14,.24,.52,.06,"e"]] },
  plank_door:   { w: .58, h: .86, lift: 0,    body: "#3d2c1c", top: "#5a422a", edge: "#8a6b46", parts: [[.04,.00,.92,1,"b"],[.12,.06,.32,.88,"t"],[.54,.06,.32,.88,"t"],[.74,.46,.12,.10,"e"]] },

  // ── LEGENDARY ─────────────────────────────────────────────────────
  chao_pool:      { w: .92, h: .62, lift: 0,  body: "#12242e", top: "#1c3d4e", edge: "#3fb6d4", lamp: "#3fb6d4", parts: [[.00,.10,1,.90,"b","44%"],[.08,.22,.84,.66,"t","46%"],[.24,.36,.52,.34,"e","50%"]] },
  anima_tree:     { w: .78, h: .96, lift: 0,  body: "#1e2f2a", top: "#2f4a40", edge: "#6ad4a8", lamp: "#6ad4a8", parts: [[.45,.70,.10,.30,"b"],[.06,.30,.88,.42,"e","48% 48% 38% 38%"],[.16,.10,.68,.40,"e","50%"],[.32,.00,.36,.28,"e","50%"],[.30,.34,.40,.26,"t","50%"]] },
  crest_fountain: { w: .76, h: .70, lift: 0,  body: "#2e2a3a", top: "#45405a", edge: "#d4a03c", lamp: "#d4a03c", parts: [[.06,.72,.88,.28,"b","44%"],[.12,.62,.76,.18,"e","50%"],[.38,.34,.24,.32,"b"],[.22,.20,.56,.20,"e","50%"],[.42,.02,.16,.24,"t","46%"]] },
  void_bench:     { w: .86, h: .44, lift: 0,  body: "#1c1a2c", top: "#2c2942", edge: "#8a6fd4", lamp: "#8a6fd4", parts: [[.04,.26,.92,.18,"t","3px"],[.06,.14,.88,.14,"e","3px 3px 0 0"],[.10,.44,.09,.56,"e"],[.81,.44,.09,.56,"e"]] },
  star_lamp:      { w: .30, h: .88, lift: 0,  body: "#22222c", top: "#34343f", edge: "#f2e07a",  lamp: "#f2e07a", parts: [[.44,.34,.12,.66,"b"],[.22,.02,.56,.34,"e","46%"],[.34,.10,.32,.18,"t","50%"]] },
  pale_pillar:    { w: .40, h: 1.1, lift: 0,  body: "#3a3a44", top: "#585866", edge: "#c8c8d8",   lamp: "#c8c8d8", parts: [[.24,.08,.52,.84,"b"],[.14,.00,.72,.12,"t"],[.14,.88,.72,.12,"t"]] },
  brass_door:     { w: .62, h: .92, lift: 0,  body: "#4a3a18", top: "#6d5626", edge: "#d4a03c", lamp: "#d4a03c", parts: [[.04,.00,.92,1,"b"],[.14,.08,.72,.84,"t"],[.42,.44,.16,.12,"e"],[.14,.08,.72,.05,"e"]] },

  // ── ⭐ OW-34 · THE ARCHITECT'S PIECES ─────────────────────────────
  // (pictures for the shed; a wall piece is drawn as it hangs)
  parlour_rug:     { w: .94, h: .70, lift: 0, body: "#4a1a2a", top: "#6a2a3e", edge: "#c9a84c", flat: true, parts: [[.00,.00,1,1,"e"],[.07,.10,.86,.80,"b"],[.14,.20,.72,.60,"t"],[.40,.38,.20,.24,"e","50%"]] },
  hall_runner:     { w: .46, h: .96, lift: 0, body: "#2a1a40", top: "#3a2458", edge: "#c9a84c", flat: true, parts: [[.00,.00,1,1,"e"],[.10,.04,.80,.92,"b"],[.36,.16,.28,.12,"e","50%"],[.36,.44,.28,.12,"e","50%"],[.36,.72,.28,.12,"e","50%"]] },
  folding_screen:  { w: .92, h: .78, lift: 0, body: "#2a2040", top: "#3a2e52", edge: "#8a6b46", parts: [[.00,.04,.32,.96,"e"],[.34,.00,.32,1,"e"],[.68,.04,.32,.96,"e"],[.04,.10,.24,.80,"t"],[.38,.06,.24,.88,"b"],[.72,.10,.24,.80,"t"]] },
  timber_arch:     { w: .74, h: .98, lift: 0, body: "#3d2c1c", top: "#5a422a", edge: "#8a6b46", parts: [[.04,.20,.14,.80,"b"],[.82,.20,.14,.80,"b"],[.04,.00,.92,.34,"e","50% 50% 0 0"],[.18,.10,.64,.30,"t","50% 50% 0 0"]] },
  hedge_row:       { w: .96, h: .56, lift: 0, body: "#1c3a1a", top: "#3e7a34", edge: "#8ac85a", parts: [[.00,.18,.36,.82,"b","40%"],[.30,.10,.40,.90,"t","40%"],[.64,.16,.36,.84,"b","40%"],[.20,.02,.60,.30,"e","50%"]] },
  wall_banner:     { w: .44, h: .86, lift: .12, body: "#3a1a52", top: "#5a2a7a", edge: "#c9a84c", parts: [[.00,.00,1,.06,"e"],[.10,.06,.80,.76,"t"],[.10,.80,.36,.18,"t","0 0 50% 0"],[.54,.80,.36,.18,"t","0 0 0 50%"],[.36,.30,.28,.24,"e","3px"]] },
  wall_sconce:     { w: .30, h: .52, lift: .45, body: "#221c2c", top: "#3a3040", edge: "#d4a03c", lamp: "#ffb04a", parts: [[.40,.40,.20,.60,"b"],[.20,.30,.60,.16,"e","40%"],[.32,.00,.36,.32,"e","50% 50% 40% 40%"]] },
  wall_shelf:      { w: .70, h: .44, lift: .40, body: "#4a3524", top: "#6a4d34", edge: "#8a6b46", parts: [[.00,.70,1,.12,"t"],[.08,.10,.12,.60,"e"],[.22,.18,.10,.52,"b"],[.34,.06,.12,.64,"e"],[.62,.36,.16,.34,"t","30%"],[.10,.82,.08,.18,"b"],[.82,.82,.08,.18,"b"]] },
  grand_carpet:    { w: .98, h: .76, lift: 0, body: "#170c28", top: "#40206a", edge: "#f0d060", flat: true, parts: [[.00,.00,1,1,"b"],[.06,.08,.88,.84,"t"],[.34,.30,.32,.40,"e","50%"],[.44,.42,.12,.16,"b","50%"]] },
  stone_partition: { w: .98, h: .62, lift: 0, body: "#8e84a6", top: "#cfc6da", edge: "#d4a03c", parts: [[.00,.14,1,.86,"b"],[.00,.00,1,.12,"t"],[.00,.10,1,.04,"e"],[.02,.18,.30,.34,"t"],[.36,.18,.30,.34,"t"],[.70,.18,.28,.34,"t"],[.18,.56,.30,.34,"t"],[.52,.56,.30,.34,"t"]] },
  pale_archway:    { w: .76, h: 1.06, lift: 0, body: "#8b8478", top: "#e8e2d2", edge: "#d4a03c", parts: [[.04,.24,.16,.76,"t"],[.80,.24,.16,.76,"t"],[.00,.02,1,.34,"b","50% 50% 0 0"],[.16,.12,.68,.30,"t","50% 50% 0 0"],[.44,.00,.12,.12,"e"]] },
  grand_tapestry:  { w: .96, h: .80, lift: .15, body: "#140a24", top: "#3a1a4e", edge: "#c9a84c", lamp: "#f2c14e", parts: [[.00,.00,1,.05,"e"],[.04,.05,.92,.86,"t"],[.36,.12,.28,.28,"b","50%"],[.04,.60,.92,.06,"e"],[.04,.66,.92,.25,"b"],[.10,.91,.06,.09,"e"],[.47,.91,.06,.09,"e"],[.84,.91,.06,.09,"e"]] },

  // ── ⭐ ROOM FURNITURE ─────────────────────────────────────────────
  plank_table:      { w: .94, h: .52, lift: 0, body: "#4a3524", top: "#6a4d34", edge: "#8a6b46", parts: [[.00,.20,1,.16,"t"],[.06,.36,.08,.64,"b"],[.86,.36,.08,.64,"b"],[.14,.60,.72,.06,"e"]] },
  tall_bookcase:    { w: .92, h: .96, lift: 0, body: "#3a2a1c", top: "#6a4d34", edge: "#c9a84c", parts: [[.00,.00,1,1,"t"],[.06,.06,.88,.20,"b"],[.06,.30,.88,.20,"b"],[.06,.54,.88,.20,"b"],[.06,.78,.88,.18,"b"],[.10,.10,.08,.16,"e"],[.30,.34,.10,.16,"e"],[.60,.58,.08,.16,"e"]] },
  straw_bed:        { w: .70, h: .96, lift: 0, body: "#6a5a32", top: "#5a2a7a", edge: "#e8e0d0", parts: [[.00,.00,1,.14,"b"],[.06,.14,.88,.20,"e","30%"],[.04,.34,.92,.62,"t","6%"]] },
  pale_hearth:      { w: .96, h: .96, lift: 0, body: "#8b8478", top: "#e8e2d2", edge: "#ff9a3a", lamp: "#ff9a3a", parts: [[.14,.00,.72,.40,"t"],[.00,.40,1,.10,"t"],[.02,.50,.26,.50,"t"],[.72,.50,.26,.50,"t"],[.28,.50,.44,.50,"b"],[.38,.70,.24,.26,"e","50% 50% 20% 20%"]] },
  void_four_poster: { w: .96, h: .98, lift: 0, body: "#1a0e08", top: "#5a2a7a", edge: "#b48aff", parts: [[.00,.00,1,.10,"t"],[.02,.00,.06,1,"b"],[.92,.00,.06,1,"b"],[.10,.46,.80,.20,"e","20%"],[.10,.64,.80,.34,"t"]] },
  long_table:       { w: .98, h: .60, lift: 0, body: "#1a0e08", top: "#3a2418", edge: "#c9a84c", lamp: "#ffc978", parts: [[.00,.34,1,.14,"t"],[.04,.48,.06,.52,"b"],[.47,.48,.06,.52,"b"],[.90,.48,.06,.52,"b"],[.20,.00,.04,.34,"e"],[.76,.00,.04,.34,"e"]] },
};

export const ART_2D_IDS = Object.keys(PROP_2D);

// ════════════════════════════════════════════════════════════════════
// THE AUDIT  —  a gap gets named at boot, never discovered by a player
// ────────────────────────────────────────────────────────────────────
// Mirrors `auditPropArt()` in the 3D kit. A catalogue row that can be
// PLACED IN THE HALL and has no 2D recipe would render as a blank patch
// of floor that swallows a piece the player paid for, so it is worth a
// boot-time line in the console.
// ════════════════════════════════════════════════════════════════════
export function auditProp2D() {
  const missing = [];
  for (const [id, it] of Object.entries(ITEMS)) {
    if (!it.areas?.includes(AREAS.HALL)) continue;
    const key = artOf(id);
    if (!key || !PROP_2D[key]) missing.push(`${id} → ${key || "(no art)"}`);
  }
  if (missing.length) {
    // eslint-disable-next-line no-console
    console.warn("[mineriumProps2D] hall-placeable items with no 2D recipe:", missing);
  }
  return missing;
}

// ════════════════════════════════════════════════════════════════════
// ⚑ THE ONE COMPONENT EVERYTHING ELSE MOUNTS
// ────────────────────────────────────────────────────────────────────
// Nothing outside this file reaches into `PROP_2D`, so the Mythical
// resolution cannot be bypassed by a caller who "just needs the shape."
//
// `frame` is the Hall's existing animation counter. `sel` draws the
// build-mode selection ring. `ghost` draws the not-yet-placed preview.
// ════════════════════════════════════════════════════════════════════
// ⚠ `rot` IS NOT OPTIONAL DECORATION — IT IS A PROMISE THE UI MAKES.
// This shipped without it while the Hall's bubble said "R turns it", the
// R key fired a real Firebase write and a TURN button sat on screen:
// three affordances for a rotation the renderer could not draw. Either
// the renderer turns or the affordances go; it turns.
// ⛑ `lift` is the height a STACKED piece sits at — `resolvePlacement()`
// returns it and the Hall was throwing it away, so a torch balanced on a
// barrel drew INSIDE the barrel. caP'n's "a torch inside a stuffed
// animal looks like it's on fire" is the whole point of stacking, and it
// was invisible indoors. Both found by adversarial review 2026-09-13.
export function MineriumProp2D({ itemId, tile, frame = 0, rot = 0, lift = 0,
                                 sel = false, ghost = false, blocked = false }) {
  const item = ITEMS[itemId];
  const key = artOf(itemId);
  const r = key ? PROP_2D[key] : null;
  if (!item || !r) return null;

  const shelfTint = (SHELF_META[item.shelf] || SHELF_META.fable).tint;
  const risen = isAscended(itemId);
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.06);

  const w = tile * r.w;
  const h = tile * r.h;

  // A flat thing is walked ON, so it is drawn centred in its tile with
  // no standee, no shadow and no lift — and under everything else.
  if (r.flat) {
    return (
      <div style={{
        position: "absolute",
        left: (tile - w) / 2, top: (tile - h) / 2,
        width: w, height: h,
        borderRadius: 4,
        background: `linear-gradient(160deg, ${r.top}, ${r.body})`,
        border: `1px solid ${r.edge}55`,
        transform: rot ? `rotate(${rot}rad)` : undefined,
        opacity: ghost ? 0.45 : 0.9,
        boxShadow: sel ? `0 0 0 2px ${shelfTint}, 0 0 10px ${shelfTint}66` : "none",
        pointerEvents: "none",
      }}>
        {/* ⚠ The flat branch took an early return and never read `parts`,
            so the rug's weave and the path's stones were dead data. A
            thing you walk over still has a pattern on it. */}
        {(r.parts || []).map((pt, pi) => (
          <div key={pi} style={{
            position: "absolute",
            left: `${pt[0] * 100}%`, top: `${pt[1] * 100}%`,
            width: `${pt[2] * 100}%`, height: `${pt[3] * 100}%`,
            background: pt[4] === "t" ? r.top : pt[4] === "e" ? r.edge : r.body,
            borderRadius: pt[5] || 2,
            opacity: pt[4] === "e" ? 0.5 : 0.7,
          }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{
      position: "absolute",
      left: (tile - w) / 2,
      // Stood on the BOTTOM of its tile, so a taller piece grows upward
      // and its base still reads as sitting on the tile it occupies.
      // `lift` raises a stacked piece off its base; `r.lift` is the
      // recipe's own resting offset. Both are in tiles.
      top: tile - h - tile * ((r.lift || 0) + (Number(lift) || 0)),
      width: w, height: h,
      pointerEvents: "none",
      // ⛑ A standee turns about its BASE, not its middle — rotating a
      // chair around its centre lifts it off the floor and it reads as
      // floating rather than turning. Yaw is faked as a horizontal
      // squash, which is what a rotation looks like from this angle.
      transform: rot ? `perspective(${tile * 6}px) rotateY(${rot}rad)` : undefined,
      transformOrigin: "50% 100%",
      opacity: ghost ? (blocked ? 0.35 : 0.55) : 1,
      filter: blocked ? "grayscale(0.7)" : "none",
    }}>
      {/* contact shadow — what stops a standee looking like a sticker */}
      <div style={{
        position: "absolute", left: "50%", bottom: -tile * 0.05,
        transform: "translateX(-50%)",
        width: w * 0.92, height: tile * 0.13,
        borderRadius: "50%",
        background: "radial-gradient(ellipse at center, rgba(0,0,0,0.62), transparent 70%)",
      }} />

      {/* the lamp glow, for anything that is a light source. ⚠ This is a
          BACKGROUND, never a light — same law as the 3D kit: a Minerium
          full of lanterns must cost nothing. */}
      {r.lamp && !ghost && (
        <div style={{
          position: "absolute", left: "50%", top: -tile * 0.12,
          transform: "translateX(-50%)",
          width: w * 2.1, height: w * 2.1,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${r.lamp}${risen ? "55" : "33"}, transparent 68%)`,
          opacity: 0.55 + pulse * 0.25,
        }} />
      )}

      {/* the body */}
      <div style={{
        position: "absolute", inset: 0,
        borderRadius: "4px 4px 3px 3px",
        background: `linear-gradient(180deg, ${r.top} 0%, ${r.body} 62%, #0b0a10 100%)`,
        border: `1px solid ${blocked ? "#e8453c" : sel ? shelfTint : r.edge + "99"}`,
        boxShadow: sel
          ? `0 0 0 2px ${shelfTint}, 0 0 12px ${shelfTint}77`
          : risen ? `0 0 ${7 + pulse * 9}px ${r.edge}66` : "none",
        overflow: "hidden",
      }}>
        {/* ⭐ THE SILHOUETTE. Fractions of this piece's own box, so one
            recipe scales to any tile size without a second number. */}
        {(r.parts || []).map((pt, pi) => (
          <div key={pi} style={{
            position: "absolute",
            left: `${pt[0] * 100}%`, top: `${pt[1] * 100}%`,
            width: `${pt[2] * 100}%`, height: `${pt[3] * 100}%`,
            background: pt[4] === "t" ? r.top : pt[4] === "e" ? r.edge : r.body,
            borderRadius: pt[5] || 1.5,
            opacity: pt[4] === "e" ? 0.92 : 1,
          }} />
        ))}
      </div>

      {/* ⭐ THE ASCENSION LAYER — the ONLY animated thing in this file,
          by the same law that governs the 3D kit: Legendary is the best
          STILL thing there is, and motion is what Mythical adds. */}
      {risen && !ghost && (
        <div style={{
          position: "absolute", left: -w * 0.18, top: -h * 0.12,
          width: w * 1.36, height: h * 1.24,
          borderRadius: 6,
          background: `conic-gradient(from ${frame * 2}deg, transparent, ${r.edge}66, transparent 55%, #fff4 70%, transparent)`,
          filter: "blur(2.5px)",
          opacity: 0.62,
          mixBlendMode: "screen",
        }} />
      )}
    </div>
  );
}

export default MineriumProp2D;
